import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/hooks/useCheckout";
import { Reveal, staggerParent, staggerChild } from "@/components/Motion";
import { Extras } from "@/components/pricing/Extras";
import { Slider } from "@/components/ui/slider";
import { Loader2, Infinity as InfinityIcon, Store, TrendingDown, Mail } from "lucide-react";

const TIERS = [["1–4 dealerships", 200], ["5–9 dealerships", 175], ["10–19 dealerships", 150], ["20–49 dealerships", 125], ["50–99 dealerships", 100]];
const CONTACT = "mailto:oscarsverse@gmail.com?subject=Vorra%20pricing%20for%20100%2B%20dealerships";
export const perDealership = (q) => (q >= 50 ? 100 : q >= 20 ? 125 : q >= 10 ? 150 : q >= 5 ? 175 : 200);
const money = (n) => `£${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;

function TierTable({ qty }) {
  return (
    <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true }} className="bg-white rounded-3xl border border-navy/10 overflow-hidden" data-testid="dealer-tier-table">
      {TIERS.map(([label, price]) => (
        <motion.div key={label} variants={staggerChild}
          className={`flex items-center justify-between px-6 py-5 border-b border-navy/5 transition-colors ${perDealership(qty) === price ? "bg-teal-tint" : ""}`}>
          <span className="font-medium text-navy">{label}</span>
          <span className="font-heading font-extrabold text-2xl text-navy tracking-tight">{money(price)}<span className="text-sm font-medium text-slatev"> /dealership/month</span></span>
        </motion.div>
      ))}
      <motion.div variants={staggerChild} className="flex items-center justify-between px-6 py-5">
        <span className="font-medium text-navy">100+ dealerships</span>
        <a href={CONTACT} className="font-heading font-bold text-teal inline-flex items-center gap-1.5" data-testid="tier-contact-us"><Mail className="w-4 h-4" />Contact us</a>
      </motion.div>
    </motion.div>
  );
}

function Calculator({ qty, setQty, sub, onStart, busy }) {
  const [saving, setSaving] = useState(false);
  const current = sub?.plan === "dealer_locations";
  const change = async () => {
    setSaving(true);
    try { await api.post("/subscription/locations", { quantity: qty }); toast.success(`Plan changed to ${qty} dealership${qty > 1 ? "s" : ""}.`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-navy text-white rounded-3xl p-7 lg:p-9" data-testid="dealer-calculator">
      <span className="text-xs uppercase tracking-widest font-semibold text-white/60">How many dealerships?</span>
      <div className="mt-4 flex items-baseline gap-2"><span className="font-heading font-extrabold text-6xl tracking-tighter" data-testid="calc-qty">{qty}</span><span className="text-white/70">dealership{qty > 1 ? "s" : ""}</span></div>
      <Slider value={[qty]} min={1} max={99} step={1} onValueChange={([v]) => setQty(v)} className="mt-6" data-testid="calc-slider" />
      <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
        <div><div className="text-white/60">Per dealership</div><div className="font-heading font-bold text-2xl" data-testid="calc-per-dealership">{money(perDealership(qty))}</div></div>
        <div><div className="text-white/60">Per month</div><div className="font-heading font-bold text-2xl" data-testid="calc-total">{money(perDealership(qty) * qty)}</div></div>
        <div><div className="text-white/60">Cars</div><div className="font-heading font-bold text-2xl inline-flex items-center gap-1" data-testid="calc-cars"><InfinityIcon className="w-6 h-6" />Unlimited</div></div>
      </div>
      <p className="mt-4 text-xs text-white/60" data-testid="calc-formula">{qty} × {money(perDealership(qty))} = {money(perDealership(qty) * qty)} a month, however many cars you list.</p>
      <button onClick={current ? change : () => onStart(qty)} disabled={saving || !!busy} data-testid={current ? "calc-change" : "calc-start"}
        className="mt-6 w-full h-12 rounded-full bg-teal hover:bg-[#347a8c] font-semibold inline-flex items-center justify-center transition-transform hover:-translate-y-0.5">
        {saving || busy === "dealer_locations" ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? `Change my plan to ${qty} dealership${qty > 1 ? "s" : ""}` : `Start with ${qty} dealership${qty > 1 ? "s" : ""}`}
      </button>
      {current && <p className="text-xs text-white/60 mt-3" data-testid="calc-current">Current plan: {sub.quantity} dealership{sub.quantity > 1 ? "s" : ""}, {sub.live_count} cars live.</p>}
      <p className="text-xs text-white/60 mt-3">100+ dealerships? <a href={CONTACT} className="underline text-white" data-testid="calc-contact-us">Contact us</a></p>
    </div>
  );
}

const EXAMPLE = [10, 25, 50, 100];
function WorkedExample() {
  return (
    <section className="mt-24 grid lg:grid-cols-2 gap-10 items-center" data-testid="worked-example">
      <Reveal>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-teal"><TrendingDown className="w-4 h-4" />The more you sell, the less you pay</span>
        <h2 className="font-heading font-extrabold text-navy tracking-tighter text-3xl sm:text-5xl mt-2">One flat price per dealership. No matter how many cars.</h2>
        <p className="text-slatev mt-4 max-w-lg">List your whole forecourt. Your fee stays the same, so every extra car you sell brings your cost per car down.</p>
        <p className="text-slatev mt-4 max-w-lg" data-testid="example-account">Running 12 dealerships? You're in the 10–19 tier: <strong className="text-navy">12 × £150 = £1,800 a month</strong>, with unlimited cars at every site.</p>
      </Reveal>
      <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="bg-white rounded-3xl border border-navy/10 p-6 lg:p-8">
        <div className="flex items-center gap-2 text-sm text-slatev"><Store className="w-4 h-4" />One dealership at £200 a month</div>
        {EXAMPLE.map((cars) => (
          <motion.div key={cars} variants={staggerChild} className="mt-5" data-testid={`example-${cars}`}>
            <div className="flex justify-between text-sm"><span className="font-medium text-navy">Sell {cars} cars a month</span><span className="font-heading font-bold text-navy">{money(200 / cars)} per car</span></div>
            <div className="h-2 rounded-full bg-teal-tint mt-2 overflow-hidden">
              <motion.div className="h-full bg-teal rounded-full" initial={{ width: 0 }} whileInView={{ width: `${(10 / cars) * 100}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} />
            </div>
          </motion.div>
        ))}
        <p className="text-xs text-slatev mt-6">No commission on any sale. Same £200, whether you sell 10 cars or 100.</p>
      </motion.div>
    </section>
  );
}

export default function DealerPricing() {
  const { user } = useAuth();
  const { busy, checkout } = useCheckout();
  const [qty, setQty] = useState(1);
  const [sub, setSub] = useState(null);
  useEffect(() => { if (user) api.get("/subscription").then(({ data }) => { setSub(data); if (data.plan === "dealer_locations" && data.quantity) setQty(data.quantity); }).catch(() => {}); }, [user]);
  const start = (q) => checkout("dealer_locations", { quantity: q });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20 overflow-x-clip" data-testid="dealer-pricing-page">
      <Reveal className="max-w-3xl">
        <span className="text-xs uppercase tracking-widest font-semibold text-teal">Dealerships · Shop</span>
        <h1 className="font-heading font-extrabold text-navy tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-2">One flat price per dealership.<br />Unlimited cars.</h1>
        <p className="text-slatev mt-5 text-base sm:text-lg">From £200 per dealership a month, falling to £100 as your group grows. List as many cars as you like, with no commission, no setup fee, and you can cancel anytime.</p>
        {user?.seller_type === "private" && <p className="mt-4 text-sm text-amber-700" data-testid="dealer-private-note">You're signed in as a private seller. Dealer plans need a dealer account.</p>}
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-8 mt-12 items-start">
        <TierTable qty={qty} />
        <Calculator qty={qty} setQty={setQty} sub={sub} onStart={start} busy={busy} />
      </div>
      <WorkedExample />
      <Extras audience="dealer" />
      <p className="text-center text-xs text-slatev mt-16">Test mode: use card 4242 4242 4242 4242, any future expiry and CVC.</p>
    </div>
  );
}
